import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_DEFAULT_DESCRIPTION, SEO_DEFAULT_FULL_TITLE } from '@/constants/seo-defaults';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: string;
  structuredData?: Record<string, unknown>;
  /** Path-only canonical override (e.g. `/duel/saka-vs-salah-gw1-25`). Query string is never included. */
  canonicalPath?: string;
  /** Optional robots directive for programmatic pages (e.g. thin-content gate). */
  robots?: string;
  duelData?: {
    playerAName: string;
    playerBName: string;
    gameweek: number;
    playerAVotes: number;
    playerBVotes: number;
    totalVotes: number;
    playerATeam?: string;
    playerBTeam?: string;
    status?: string;
  };
}

const SEO = ({
  title,
  description,
  keywords,
  image = 'https://crowdfpl.com/og-image.png',
  imageWidth,
  imageHeight,
  type = 'website',
  structuredData,
  canonicalPath,
  robots,
}: SEOProps) => {
  const location = useLocation();
  const canonicalUrl = canonicalPath
    ? `https://crowdfpl.com${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`
    : `https://crowdfpl.com${location.pathname}`;

  const defaultTitle = SEO_DEFAULT_FULL_TITLE;
  const defaultDescription = SEO_DEFAULT_DESCRIPTION;

  const fullTitle = title ? `${title} | CrowdFPL` : defaultTitle;
  const metaDescription = description || defaultDescription;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'CrowdFPL',
    'url': 'https://crowdfpl.com',
    'logo': 'https://crowdfpl.com/logo.png',
    'sameAs': [
      'https://twitter.com/crowdfpl',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'CrowdFPL',
    'url': 'https://crowdfpl.com',
  };

  const webApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'CrowdFPL',
    'url': 'https://crowdfpl.com',
    'applicationCategory': 'SportsApplication',
    'operatingSystem': 'Any',
    'browserRequirements': 'Requires JavaScript. A modern browser with JavaScript enabled.',
  };

  // Helper to safely serialize JSON-LD
  const asLd = (data: unknown) => ({
    type: 'application/ld+json',
    innerHTML: JSON.stringify(data).replace(/</g, '\\u003c'),
  });

  const metaTags = [
    { name: 'description', content: metaDescription },
    ...(robots ? [{ name: 'robots', content: robots }] : []),
    ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
    // Open Graph
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: metaDescription },
    { property: 'og:type', content: type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: image },
    ...(imageWidth ? [{ property: 'og:image:width', content: String(imageWidth) }] : []),
    ...(imageHeight ? [{ property: 'og:image:height', content: String(imageHeight) }] : []),
    { property: 'og:site_name', content: 'CrowdFPL' },
    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@crowdfpl' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:image', content: image },
  ];

  const scripts = [
    asLd(organizationSchema),
    asLd(websiteSchema),
    asLd(webApplicationSchema),
    ...(structuredData ? [asLd(structuredData)] : []),
  ];

  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      title={fullTitle}
      link={[{ rel: 'canonical', href: canonicalUrl }]}
      meta={metaTags}
      script={scripts}
    />
  );
};

export default SEO;
